import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { AuthPageShell } from '@/components/auth/auth-page-shell'
import { useAuth } from '@/hooks/use-auth'
import {
  completeSocialAuth,
  fetchAllauthSession,
  hasPendingProviderSignup,
} from '@/lib/social-auth'
import { resolvePostAuthRedirect } from '@/lib/auth-return'

export function AuthProviderCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { loginWithTokens } = useAuth()
  const [status, setStatus] = useState<'loading' | 'error'>('loading')
  const [message, setMessage] = useState('Completing sign-in…')

  useEffect(() => {
    const errorParam = searchParams.get('error')
    if (errorParam) {
      setStatus('error')
      setMessage('Social sign-in failed or was cancelled. Please try again.')
      return
    }

    const finish = async () => {
      try {
        const session = await fetchAllauthSession()
        if (hasPendingProviderSignup(session)) {
          navigate('/auth/provider/signup', { replace: true })
          return
        }

        const result = await completeSocialAuth()
        if (result.pending_provider_signup) {
          navigate('/auth/provider/signup', { replace: true })
          return
        }

        if (result.approval_pending) {
          navigate('/register/pending', { replace: true })
          return
        }
        if (result.approval_rejected) {
          setStatus('error')
          setMessage(result.error ?? 'This account was not approved.')
          return
        }

        if (!result.success || !result.access_token || !result.user) {
          setStatus('error')
          setMessage(result.error ?? 'Could not complete social sign-in.')
          return
        }

        loginWithTokens(result.access_token, result.user)
        const destination = resolvePostAuthRedirect({
          returnToParam: searchParams.get('returnTo'),
        })
        if (result.onboarding_redirect) {
          navigate('/onboarding', { replace: true })
        } else {
          navigate(destination, { replace: true })
        }
      } catch {
        setStatus('error')
        setMessage('Network error. Please try again.')
      }
    }

    void finish()
  }, [loginWithTokens, navigate, searchParams])

  return (
    <AuthPageShell title="Signing you in">
      <div className="flex flex-col gap-4 text-center">
        {status === 'loading' ? (
          <>
            <p className="text-lg font-semibold">{message}</p>
            <p className="text-sm text-muted-foreground">Please wait</p>
          </>
        ) : (
          <>
            <p className="p-4 text-sm text-destructive border border-destructive/20 rounded-md bg-destructive/10">
              {message}
            </p>
            <Button onClick={() => navigate('/login')} fullWidth>
              Back to sign in
            </Button>
          </>
        )}
      </div>
    </AuthPageShell>
  )
}
